import React, { useState } from 'react';
import { 
  SharedListData, 
  isStandaloneApp,
  isIOSDevice,
  prepareOpenInApp,
  savePendingSharedList,
  encodeSharedListData 
} from '../services/storageService';
import { User } from '../types';
import { GoogleLoginButton } from './GoogleAuthModal';

interface ReceiveSharedListModalProps {
  isOpen: boolean;
  sharedData: SharedListData | null;
  currentUser: User | null;
  onAccept: (sharedData: SharedListData) => void;
  onClose: () => void;
  onLogin: (user: User) => void;
}

export const ReceiveSharedListModal: React.FC<ReceiveSharedListModalProps> = ({
  isOpen,
  sharedData,
  currentUser,
  onAccept,
  onClose,
  onLogin,
}) => {
  const [showIOSTip, setShowIOSTip] = useState(false);
  const isInstalledPWA = isStandaloneApp();

  if (!isOpen || !sharedData) return null;

  const handleOpenInInstalledApp = async () => {
    // 1. Persist to pending shared list so the installed app automatically picks it up upon opening
    const raw = encodeSharedListData({
      id: sharedData.id,
      name: sharedData.name,
      ownerName: sharedData.ownerName,
      items: sharedData.items.map(i => ({
        id: i.id,
        name: i.name,
        quantity: i.quantity,
        checked: i.checked,
        category: i.category
      })),
      sharedWith: [],
      accessIds: [],
      shareToken: sharedData.token
    }, sharedData.ownerName);
    savePendingSharedList(raw);

    // 2. Kopieer de link (nodig op iPhone, waar de app eigen opslag heeft)
    await prepareOpenInApp(`${window.location.origin}/?share_list_data=${raw}`);
    setShowIOSTip(true);
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" 
        onClick={onClose} 
      />

      {/* Modal Dialog */}
      <div className="bg-white dark:bg-[#15241b] w-full max-w-sm rounded-[28px] p-6 shadow-2xl relative z-10 animate-in zoom-in-95 duration-200 border border-gray-100 dark:border-white/10 max-h-[92vh] overflow-y-auto no-scrollbar">
        
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-5">
          <div className="w-14 h-14 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-2xl shadow-sm mb-3">
            <span className="material-symbols-outlined text-3xl">shopping_cart</span>
          </div>
          
          {isInstalledPWA ? (
            <span className="text-[11px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-3 py-1 rounded-full mb-1 flex items-center gap-1.5">
              <span className="material-symbols-outlined text-[14px]">verified</span>
              Geopend in superMarct app
            </span>
          ) : (
            <span className="text-[11px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-3 py-1 rounded-full mb-1">
              Gedeelde Lijst Ontvangen
            </span>
          )}

          <h3 className="text-xl font-extrabold text-gray-900 dark:text-white leading-tight mt-1">
            {sharedData.name}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Gedeeld door <span className="font-bold text-gray-700 dark:text-gray-300">{sharedData.ownerName}</span>
          </p>
        </div>

        {/* Deep Link Quick Switch: If opened in web browser but app is installed */}
        {!isInstalledPWA && (
          <div className="mb-4 p-3.5 rounded-2xl bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950/40 dark:to-green-950/30 border border-primary/30 text-xs">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5 font-extrabold text-gray-900 dark:text-white text-xs">
                <span className="material-symbols-outlined text-primary text-[18px]">smartphone</span>
                <span>App al geïnstalleerd?</span>
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider bg-primary text-black px-2 py-0.5 rounded-full">Direct</span>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-[11px] leading-relaxed mb-2.5">
              Heb je superMarct al op je beginscherm? Schakel direct over naar de app.
            </p>
            <button
              type="button"
              onClick={handleOpenInInstalledApp}
              className="w-full py-2.5 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-bold text-xs flex items-center justify-center gap-2 hover:opacity-95 active:scale-[0.98] transition cursor-pointer shadow-sm"
            >
              <span className="material-symbols-outlined text-[16px] text-primary">open_in_new</span>
              <span>Openen in superMarct app</span>
            </button>
            {showIOSTip && (
              <p className="text-[11px] text-emerald-800 dark:text-emerald-300 mt-2 bg-emerald-100/70 dark:bg-emerald-900/40 p-2 rounded-xl">
                {isIOSDevice() ? (
                  <>✓ Link gekopieerd! Open <strong>superMarct</strong> op je beginscherm en tik op <strong>⋮ → Gedeelde link openen</strong>.</>
                ) : (
                  <>✓ Lijst klaargezet! Open <strong>superMarct</strong> op je beginscherm, de lijst verschijnt direct.</>
                )}
              </p>
            )}
          </div>
        )}

        {/* Items List Preview */}
        <div className="bg-surface-light dark:bg-black/20 rounded-2xl p-4 border border-gray-100 dark:border-white/5 mb-4 max-h-48 overflow-y-auto no-scrollbar">
          <div className="flex items-center justify-between text-xs font-bold text-gray-500 uppercase pb-2 mb-2 border-b border-gray-200 dark:border-white/10">
            <span>Artikelen ({sharedData.items?.length || 0})</span>
            <span>Aantal</span>
          </div>
          {sharedData.items && sharedData.items.length > 0 ? (
            <div className="space-y-1.5">
              {sharedData.items.map((item, idx) => (
                <div key={item.id || idx} className="flex items-center justify-between text-xs text-gray-700 dark:text-gray-300 py-0.5">
                  <div className="flex items-center gap-2 truncate pr-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></span>
                    <span className="truncate">{item.name}</span>
                  </div>
                  {item.quantity && (
                    <span className="text-[11px] font-semibold text-gray-400 bg-gray-100 dark:bg-white/10 px-1.5 py-0.5 rounded">
                      {item.quantity}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic text-center py-2">De lijst is nog leeg.</p>
          )}
        </div>

        {/* Tip for account sync if not logged in */}
        {!currentUser && (
          <div className="mb-4 p-3.5 rounded-2xl bg-primary/10 border border-primary/20 text-xs">
            <div className="flex items-center gap-2 font-bold text-primary mb-1">
              <span className="material-symbols-outlined text-[16px]">sync</span>
              <span>{sharedData.token ? 'Log in om live mee te doen' : 'Realtime samenwerken?'}</span>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-[11px] leading-relaxed mb-3">
              Koppel je Google account om deze lijst samen realtime bij te houden en af te vinken.
            </p>
            <GoogleLoginButton 
              onLogin={onLogin} 
              label="Log in met Google" 
              className="py-2.5 text-xs shadow-none"
            />
          </div>
        )}

        {/* Tip for installing the app if not standalone */}
        {!isInstalledPWA && (
          <div className="mb-5 p-3 rounded-2xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 text-xs text-gray-600 dark:text-gray-300">
            <div className="flex items-center gap-2 font-bold text-gray-800 dark:text-gray-200 mb-1">
              <span className="material-symbols-outlined text-[16px] text-primary">install_mobile</span>
              <span>Nog niet geïnstalleerd?</span>
            </div>
            <p className="text-[11px] leading-relaxed">
              Tik in Safari op <span className="font-semibold">Deel ⎋</span> en kies <span className="font-semibold">'Zet op beginscherm'</span> (of in Chrome op de 3 puntjes en kies <span className="font-semibold">'App installeren'</span>).
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-2">
          <button
            onClick={() => onAccept(sharedData)}
            className="w-full py-4 rounded-2xl bg-primary text-black font-extrabold text-sm shadow-lg shadow-green-500/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2 cursor-pointer"
          >
            <span className="material-symbols-outlined text-lg">{sharedData.token && currentUser ? 'group' : 'add_task'}</span>
            <span>{sharedData.token && currentUser ? 'Live meedoen met deze lijst' : 'Lijst toevoegen & openen'}</span>
          </button>

          <button
            onClick={onClose}
            className="w-full py-2.5 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 font-medium transition text-center"
          >
            Niet nu / Sluiten
          </button>
        </div>

      </div>
    </div>
  );
};
